import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Lock, TrendingUp, TrendingDown, Bitcoin } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type Coin = {
  id: string;
  symbol: string;
  name: string;
  price: number;
  change24h: number | null;
  change7d: number | null;
  change1h: number | null;
  marketCap: number;
  volume24h: number;
  image: string;
  high24h: number;
  low24h: number;
};

const formatUsd = (n: number) => {
  if (n >= 1) return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `$${n.toPrecision(4)}`;
};

const formatMarketCap = (n: number) => {
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toLocaleString()}`;
};

const PctBadge = ({ value }: { value: number | null }) => {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  const isUp = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-mono font-semibold ${isUp ? "text-green-400" : "text-red-400"}`}>
      {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isUp ? "+" : ""}{value.toFixed(2)}%
    </span>
  );
};

const CryptoMonitor = () => {
  const { isPremium } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["crypto-data"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-crypto");
      if (error) throw error;
      return data as { coins: Coin[]; fetchedAt: string };
    },
    staleTime: 120000,
    refetchInterval: isPremium ? 120000 : 600000,
    retry: 2,
    placeholderData: (prev) => prev,
  });

  const coins = data?.coins || [];

  // Stablecoin peg alert
  const stablecoinAlert = coins.find(
    (c) => ["USDT", "USDC"].includes(c.symbol) && Math.abs(c.price - 1) > 0.005
  );

  return (
    <section id="crypto-monitor" className="py-6 sm:py-8">
      <div className="container mx-auto px-4">
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-1">
            <Bitcoin className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            <h2 className="font-mono text-sm sm:text-lg font-bold uppercase tracking-wider text-foreground">Crypto Monitor</h2>
          </div>
          {stablecoinAlert && (
            <Badge variant="outline" className="text-[9px] font-mono text-red-400 border-red-400/30 bg-red-500/10 animate-pulse">
              ⚠ {stablecoinAlert.symbol} DE-PEG: ${stablecoinAlert.price.toFixed(4)}
            </Badge>
          )}
        </div>

        {isLoading && !data ? (
          <div className="rounded-lg border border-border bg-card p-6 sm:p-8 text-center text-xs sm:text-sm text-muted-foreground">
            Loading crypto data...
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            {/* Header - desktop only */}
            <div className="hidden sm:grid grid-cols-[1fr_90px_70px_70px_90px] gap-2 px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground border-b border-border">
              <span>Asset</span>
              <span className="text-right">Price</span>
              <span className="text-right">1h</span>
              <span className="text-right">24h</span>
              <span className="text-right">Mkt Cap</span>
            </div>

            <div className="divide-y divide-border/50">
              {coins.map((c, i) => {
                const isBlurred = !isPremium && i >= 4;
                return (
                  <div
                    key={c.id}
                    className={`flex items-center sm:grid sm:grid-cols-[1fr_90px_70px_70px_90px] gap-2 px-2.5 sm:px-3 py-2 sm:py-2.5 transition-colors hover:bg-accent/50 ${isBlurred ? "blur-sm select-none" : ""}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1 sm:flex-none">
                      <img src={c.image} alt={c.name} className="h-5 w-5 sm:h-6 sm:w-6 rounded-full" loading="lazy" />
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-foreground truncate">{c.name}</p>
                        <div className="flex items-center gap-1.5 sm:hidden">
                          <span className="text-[10px] font-mono text-muted-foreground">{c.symbol}</span>
                          <PctBadge value={c.change24h} />
                        </div>
                        <p className="text-[10px] font-mono text-muted-foreground hidden sm:block">{c.symbol}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs sm:text-sm font-mono font-semibold text-foreground">{formatUsd(c.price)}</p>
                    </div>
                    <div className="text-right hidden sm:block">
                      <PctBadge value={c.change1h} />
                    </div>
                    <div className="text-right hidden sm:block">
                      <PctBadge value={c.change24h} />
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="text-xs font-mono text-muted-foreground">{formatMarketCap(c.marketCap)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!isPremium && (
          <div className="mt-3 rounded-md border border-primary/20 bg-primary/5 p-3 text-center">
            <p className="text-xs text-muted-foreground">
              <Lock className="mr-1 inline h-3 w-3" />
              Free users see top 4 coins. <span className="text-primary font-semibold cursor-pointer">Upgrade to Premium</span> for all 10+ with 2-min refresh.
            </p>
          </div>
        )}

        {data?.fetchedAt && (
          <p className="mt-2 text-[10px] font-mono text-muted-foreground text-right">
            Last updated: {new Date(data.fetchedAt).toLocaleTimeString()}
          </p>
        )}
      </div>
    </section>
  );
};

export default CryptoMonitor;
